import styled, { css } from 'styled-components/macro'

import { FlashOn } from '@material-ui/icons'
import * as React from 'react'

import { FlexBox } from '../../../atoms/Box/Box'
import { Icon } from '../../../atoms/Icon'
import { getRadiusValue, TRadius } from '../../../designSystem'
import { cn } from '../../../libs/classnames'
import { Colors, Radiuses } from '../../../styling'
import { IconHandoverTime } from '../../../ui/Icons'
import { useOrderHandoverTime } from '../hooks/useOrderHandoverTime'
import { TOrderForHandoverTime } from '../typings/handoverTime.types'

type TStyledProps = {
  radius: TRadius
  bgColor: TColor
  textColor: TColor
}

type TProps = NoChildren & {
  radius?: TRadius
  onClick?: () => void
  onTimeHighlight?: boolean
  order: TOrderForHandoverTime
  isWithIcon?: boolean
}

const BADGE_SIZE = 2

export const onTimeOrderBadgeStyles = css`
  border: var(--box-spacing) solid ${Colors.white.main};
  box-shadow: 0 0 0 3px ${Colors.orange.main};
`

const StyledHandoverTimeBadge = styled.div<TStyledProps>`
  position: relative;
  --box-spacing: 3px;
  --dimensions: calc(${BADGE_SIZE}em + 2 * var(--box-spacing));

  display: grid;
  place-items: center;

  background: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};

  width: var(--dimensions);
  height: var(--dimensions);
  border-radius: ${({ radius }) => getRadiusValue(radius)};

  line-height: 1;
  font-size: 1em;
  white-space: nowrap;

  /* background-clip: padding-box; Safari */

  &.on-time {
    ${onTimeOrderBadgeStyles}
  }

  &.small-font span {
    font-size: 0.875em;
  }

  &.clickable {
    cursor: pointer;
  }
`

export const HandoverTimeBadge: React.FC<TProps> = ({
  order,
  onClick,
  radius = `pill`,
  onTimeHighlight = true,
  isWithIcon = false,
}) => {
  const {
    bgColor, //
    textColor,
    outputShort,
    isOrderOnTime,
    outputShortLength,
  } = useOrderHandoverTime({
    order,
    iconSizeUnit: 'em',
    iconSize: BADGE_SIZE,
  })

  const shouldShowOnTimeIcon =
    isWithIcon && isOrderOnTime && !order.isExpressDelivery
  const shouldShowExpressIcon = isWithIcon && order.isExpressDelivery

  return (
    <StyledHandoverTimeBadge
      radius={radius}
      bgColor={bgColor}
      onClick={onClick}
      textColor={textColor}
      className={cn(
        { 'on-time': onTimeHighlight && isOrderOnTime },
        { clickable: Boolean(onClick) },
        { 'small-font': outputShortLength > 2 },
      )}
    >
      <BadgeIcon
        icon={IconHandoverTime}
        isWithBoxShadow
        isVisible={shouldShowOnTimeIcon}
      />
      <BadgeIcon
        icon={FlashOn}
        iconColor={Colors.yellow.main}
        isVisible={shouldShowExpressIcon}
      />
      <span>{outputShort}</span>
    </StyledHandoverTimeBadge>
  )
}

// HELPERS
const BADGE_ICON_SIZE = `18px`
const POSITION_VALUE_WITH_SHADOW = `22px`
const POSITION_VALUE_WITHOUT_SHADOW = `25px`

type TIconProps = PropsOf<typeof Icon>

type TBadgeIconProps = NoChildren & {
  icon: TIconProps['icon']
  iconColor?: TIconProps['color']
  isWithBoxShadow?: boolean
  isVisible?: boolean
}

// A small icon used to indicate the order is on time or express delivery
const BadgeIcon: React.FC<TBadgeIconProps> = ({
  icon,
  iconColor,
  isWithBoxShadow = false,
  isVisible = true,
}) => {
  if (!isVisible) {
    return null
  }

  const positionValue = isWithBoxShadow
    ? POSITION_VALUE_WITH_SHADOW
    : POSITION_VALUE_WITHOUT_SHADOW

  return (
    <FlexBox
      bgcolor={Colors.blue.dark}
      width={BADGE_ICON_SIZE}
      height={BADGE_ICON_SIZE}
      justifyContent={`center`}
      alignContent={`center`}
      borderRadius={Radiuses.rounded}
      position={`absolute`}
      bottom={positionValue}
      left={positionValue}
      padding={0}
    >
      <Icon icon={icon} color={iconColor} fontSize={12} fontSizeUnit={`px`} />
    </FlexBox>
  )
}
